import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { Box, ListItem, SectionHeader, useTheme } from "arkh";
import React, { useCallback, useLayoutEffect } from "react";
import { ScrollView } from "react-native";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { AccountEditQuery } from "./__generated__/AccountEditQuery.graphql";
import { useGetRoleLabels } from "../UserAdmin/RoleLabels";
import {
  MoreItem,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { useAlert } from "@/src/common/Alert";
import { useIntl } from "react-intl";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  AccountEditStack,
  AccountEditScreenProps,
} from "./AccountEditNavigation";
import { AccountEditRoleScreen } from "./AccountEditRoleScreen";
import { AccountEditDisconnectEmployeeMutation } from "./__generated__/AccountEditDisconnectEmployeeMutation.graphql";
import { AccountEditConnectEmployeeScreen } from "./AccountEditConnectEmployeeScreen";
import { AccountEditSetRoleMutation } from "./__generated__/AccountEditSetRoleMutation.graphql";
import { AccountEditAccountOwnerCard } from "./AccountEditAccountOwnerCard";
import { AccountEditDeleteAccountMutation } from "./__generated__/AccountEditDeleteAccountMutation.graphql";
import { AccountEditInviteCard } from "./AccountEditInviteCard";

function InnerAccountEditScreen(
  props: {
    fetchKey: string;
  } & AccountEditScreenProps<"AccountEditScreen">
) {
  const data = useLazyLoadQuery<AccountEditQuery>(
    graphql`
      query AccountEditQuery($id: ID!, $storeId: ID) {
        store(id: $storeId) {
          storeUser(id: $id) {
            id
            role
            user {
              id
              name
              email
              isViewer
              isOrgAdminViewer
              invite {
                expiresAt
                createdAt
              }
            }
            employee {
              id
              ...AccountEditAccountOwnerCard_employee
            }
          }
        }
      }
    `,
    {
      id: props.route.params.id,
      storeId: props.route.params.storeId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [deleteAccount] = useMutation<AccountEditDeleteAccountMutation>(graphql`
    mutation AccountEditDeleteAccountMutation($id: ID!, $storeId: ID) {
      store(id: $storeId) {
        storeUser(id: $id) {
          remove
        }
      }
    }
  `);

  const [disconnectEmployee] =
    useMutation<AccountEditDisconnectEmployeeMutation>(graphql`
      mutation AccountEditDisconnectEmployeeMutation($id: ID!, $storeId: ID) {
        store(id: $storeId) {
          storeUser(id: $id) {
            unlinkEmployee {
              id
              employee {
                id
              }
            }
          }
        }
      }
    `);

  const [setRole] = useMutation<AccountEditSetRoleMutation>(graphql`
    mutation AccountEditSetRoleMutation($id: ID!, $role: Roles!, $storeId: ID) {
      store(id: $storeId) {
        storeUser(id: $id) {
          setRole(role: $role) {
            id
            role
          }
        }
      }
    }
  `);
  const storeUser = data.store?.storeUser;
  const user = storeUser?.user;
  const isViewer = user?.isViewer;
  const role = storeUser?.role;
  const hasEmployee = storeUser?.employee;
  const isGroupAccount = role === "COWORKER_GROUP";
  const alert = useAlert();
  const intl = useIntl();
  const getRoleLabels = useGetRoleLabels();
  const intl_tokens = useIntlTokens();
  const getNavigationHeaderOptions = useNavigationHeaderOptions();

  const ConvertToPersonalAccount = intl.formatMessage({
    defaultMessage: "Convert to Personal Account",
    description: "edit user disconnect employee more item label",
  });
  const ConvertToGroupAccount = intl.formatMessage({
    defaultMessage: "Convert to Group Account",
    description: "edit user disconnect employee more item label",
  });

  const [resendInvite, resendInviteLoading] = useMutation(graphql`
    mutation AccountEditInviteUserMutation($id: ID!) {
      store {
        storeUser(id: $id) {
          resendInvite {
            id
            user {
              id
              invite {
                expiresAt
                createdAt
              }
            }
          }
        }
      }
    }
  `);

  const openDeleteAlert = useCallback(() => {
    if (!storeUser) {
      return;
    }
    alert.alert(
      intl_tokens.DeleteAccount,
      intl.formatMessage({
        defaultMessage: "Are you sure you want to delete this account?",
        description: "edit user delete account alert description",
      }),
      [
        {
          text: intl_tokens.Cancel,
          style: "cancel",
        },
        {
          text: intl_tokens.Delete,
          style: "destructive",
          onPress: () => {
            deleteAccount({
              variables: {
                id: storeUser.id,
                storeId: props.route.params.storeId,
              },
              onCompleted: () => {
                props.navigation.goBack();
              },
            });
          },
        },
      ]
    );
  }, [
    storeUser,
    props.route.params.storeId,
    alert,
    intl,
    intl_tokens.Cancel,
    intl_tokens.Delete,
    deleteAccount,
    props.navigation,
    intl_tokens.DeleteAccount,
  ]);

  const openConvertToGroupAccountAlert = useCallback(() => {
    if (!storeUser) {
      return;
    }
    alert.alert(
      ConvertToGroupAccount,
      intl.formatMessage({
        defaultMessage:
          "Are you sure you want to convert this account to a group account?",
        description: "edit user convert to group account alert description",
      }),
      [
        {
          text: intl_tokens.Cancel,
          style: "cancel",
        },
        {
          text: ConvertToGroupAccount,
          onPress: () => {
            setRole({
              variables: {
                id: props.route.params.id,
                role: "COWORKER_GROUP",
                storeId: props.route.params.storeId,
              },
            });
          },
        },
      ]
    );
  }, [
    storeUser,
    alert,
    ConvertToGroupAccount,
    intl,
    intl_tokens.Cancel,
    setRole,
    props.route.params.id,
    props.route.params.storeId,
  ]);

  const openConvertToPersonalAccountAlert = useCallback(() => {
    if (!storeUser) {
      return;
    }
    alert.alert(
      ConvertToPersonalAccount,
      intl.formatMessage({
        defaultMessage:
          "Are you sure you want to csonvert this account to a personal account?",
        description: "edit user convert to personal account alert description",
      }),
      [
        {
          text: intl_tokens.Cancel,
          style: "cancel",
        },
        {
          text: ConvertToPersonalAccount,
          onPress: () => {
            setRole({
              variables: {
                id: props.route.params.id,
                role: "CO_WORKER",
                storeId: props.route.params.storeId,
              },
            });
          },
        },
      ]
    );
  }, [
    storeUser,
    alert,
    ConvertToPersonalAccount,
    intl,
    intl_tokens.Cancel,
    setRole,
    props.route.params.id,
    props.route.params.storeId,
  ]);

  const canDeleteAndEditRole = !isViewer || user?.isOrgAdminViewer;

  useLayoutEffect(() => {
    const more: MoreItem[] = [];

    if (role === "COWORKER_GROUP") {
      more.push({
        label: ConvertToPersonalAccount,
        onPress: () => {
          openConvertToPersonalAccountAlert();
        },
      });
    }

    if (role !== "COWORKER_GROUP" && !hasEmployee) {
      more.push({
        label: ConvertToGroupAccount,
        onPress: () => {
          openConvertToGroupAccountAlert();
        },
      });
    }

    if (hasEmployee) {
      more.push({
        label: intl.formatMessage({
          defaultMessage: "Disconnect Employee",
          description: "edit user disconnect employee more item label",
        }),
        icon: "user-remove",
        color: "negative",
        onPress: () => {
          disconnectEmployee({
            variables: {
              id: storeUser.id,
              storeId: props.route.params.storeId,
            },
          });
        },
      });
    }

    if (canDeleteAndEditRole) {
      more.push({
        label: intl_tokens.DeleteAccount,
        onPress: () => {
          openDeleteAlert();
        },
        icon: "trash",
        color: "negative",
      });
    }

    props.navigation.setOptions(
      getNavigationHeaderOptions({
        type: "CLOSE",
        title: intl_tokens.Account,
        onClose: () => {
          props.navigation.goBack();
        },
        more,
      })
    );
  }, [
    props.navigation,
    intl_tokens.Account,
    intl_tokens.DeleteAccount,
    storeUser,
    hasEmployee,
    setRole,
    disconnectEmployee,
    getNavigationHeaderOptions,
    intl,
    props.route.params.id,
    openDeleteAlert,
    role,
    ConvertToPersonalAccount,
    openConvertToPersonalAccountAlert,
    openConvertToGroupAccountAlert,
    isViewer,
    ConvertToGroupAccount,
    props.route.params.storeId,
    user?.isOrgAdminViewer,
    canDeleteAndEditRole,
  ]);

  if (!user || !role) {
    return null;
  }

  return (
    <ScrollView>
      {user.invite ? (
        <Box space="medium" color="surface-primary">
          <AccountEditInviteCard
            expiresAt={user.invite.expiresAt}
            createdAt={user.invite.createdAt}
            inviteUserLoading={resendInviteLoading}
            inviteUser={() => {
              resendInvite({
                variables: {
                  id: storeUser.id,
                },
              });
            }}
          />
        </Box>
      ) : null}
      {!isGroupAccount ? (
        <AccountEditAccountOwnerCard
          employee={storeUser.employee}
          openConnectEmployee={() => {
            props.navigation.navigateDeprecated(
              "AccountEditConnectEmployeeScreen",
              {
                id: props.route.params.id,
                storeId: props.route.params.storeId,
              }
            );
          }}
          openEmployee={() => {
            if (!storeUser.employee?.id) {
              return;
            }
            props.navigation.navigateDeprecated("EmployeeEdit", {
              id: storeUser.employee?.id,
              storeId: props.route.params.storeId,
            });
          }}
        />
      ) : null}
      {user.name && user.name.trim().length > 2 && !hasEmployee ? (
        <Box space="medium">
          <SectionHeader new>
            <SectionHeader.Title>{intl_tokens.Name}</SectionHeader.Title>
          </SectionHeader>
          <ListItem isFree disabled border="border-primary">
            <ListItem.Title>{user.name}</ListItem.Title>
          </ListItem>
        </Box>
      ) : null}
      <Box space="medium">
        <SectionHeader new>
          <SectionHeader.Title>{intl_tokens.Email}</SectionHeader.Title>
        </SectionHeader>
        <ListItem border="border-primary" disabled isFree>
          <ListItem.Title selectable>{user.email}</ListItem.Title>
        </ListItem>
        {isGroupAccount ? null : (
          <Box spaceTop="medium">
            <SectionHeader new>
              <SectionHeader.Title>
                {intl_tokens.Permissions}
              </SectionHeader.Title>
            </SectionHeader>
            <ListItem
              onPress={() => {
                props.navigation.navigateDeprecated("AccountEditRoleScreen", {
                  id: props.route.params.id,
                  storeId: props.route.params.storeId,
                });
              }}
              disabled={!canDeleteAndEditRole}
              border="border-primary"
              isFree
              right={canDeleteAndEditRole ? <ListItem.NavArrow /> : null}
            >
              <ListItem.Title>{getRoleLabels(role)}</ListItem.Title>
            </ListItem>
          </Box>
        )}
      </Box>
    </ScrollView>
  );
}

function AccountEditScreen(props: AccountEditScreenProps<"AccountEditScreen">) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerAccountEditScreen fetchKey={fetchKey} {...props} />}
    </LazyQueryNetworkBoundrary>
  );
}

export const AccountEdit = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"AccountEdit">) => {
    const getOptions = useNavigationHeaderOptions();
    const intl_tokens = useIntlTokens();
    const theme = useTheme();
    return (
      <AccountEditStack.Navigator
        screenOptions={{
          contentStyle: {
            backgroundColor: theme["surface-background-light"],
          },
        }}
      >
        <AccountEditStack.Screen
          name="AccountEditScreen"
          component={AccountEditScreen}
          initialParams={{
            id: props.route.params.id,
            storeId: props.route.params.storeId,
          }}
          options={getOptions({
            type: "CLOSE",
            title: intl_tokens.Account,
            onClose: () => {
              props.navigation.goBack();
            },
          })}
        />
        <AccountEditStack.Screen
          name="AccountEditRoleScreen"
          component={AccountEditRoleScreen}
          options={getOptions({
            type: "CLOSE",
            title: intl_tokens.EditRole,
            onClose: () => {
              props.navigation.goBack();
            },
          })}
        />
        <AccountEditStack.Screen
          name="AccountEditConnectEmployeeScreen"
          component={AccountEditConnectEmployeeScreen}
          options={getOptions({
            type: "CLOSE",
            title: "",
            onClose: () => {
              props.navigation.goBack();
            },
          })}
        />
      </AccountEditStack.Navigator>
    );
  }
);
